import React, { Component } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Grid,
  List,
  Paper,
  Tab,
  Tabs,
} from '@material-ui/core';
import TabPanel from '../shared/TabPanel';
import ActionListItem from '../shared/ActionListItem';
import InProgressListItem from '../shared/InProgressListItem';
import DownloadIconButton from '../shared/DownloadIconButton';
import {
  fetchTtlCsv,
  fetchTtlCsvEntry,
  fetchTtlCsvStatus,
  fetchTtlCsvStatusAll,
} from '../../helpers/externalRequests';
import i18n from '../../i18n';
import CircularProgressButton from '../shared/CircularProgressButton';

class TimeToListReportGeneration extends Component {
  constructor(props) {
    super(props);
    this.state = {
      itemsInProgress: [],
      completedItems: [],
      myCompletedProgressItems: [],
      myItems: [],
      currentCsvDownloadTab: 0,
      loading: true,
      progress: {},
    }
  }

  componentDidMount = () => {
    this.getS3Status(true);
  }

  checkProgress = () => {
    const {
      itemsInProgress
    } = this.state;
    if (!itemsInProgress.length) {
      this.setState({ loading: false });
    } else {
      itemsInProgress.forEach(item => {
        this.handleStatusCheck(item.exportId);
      });
    }
  }

  getS3Status = async (shouldCheckProgress) => {
    const {
      myItems,
    } = this.state;

    const {
      displaySnackbar
    } = this.props;

    try {
      const s3Status = (await fetchTtlCsvStatusAll()).data;
      const myCompletedProgressItems = [];
      const updatedItemsInProgress = s3Status.inProgress ? s3Status.inProgress : [];
      const updatedCompletedItems = s3Status.completed ? s3Status.completed : [];
      // if there are any myItems, update myCompletedItems
      // or add to inProgressItems if aws still hasn't started reporting as inProgress
      myItems.forEach(item => {
        const myCompletedItem = updatedCompletedItems.find(
          completedItem => completedItem.filename.includes(item.exportId)
        );
        if (myCompletedItem) {
          myCompletedProgressItems.push(myCompletedItem);
        } else {
          const myItemInProgress = updatedItemsInProgress.find(
            inProgressItem => inProgressItem.exportId.includes(item.exportId)
          );
          if (!myItemInProgress) {
            updatedItemsInProgress.unshift(item);
          }
        }
      });
      this.setState({
        itemsInProgress: updatedItemsInProgress,
        completedItems: updatedCompletedItems,
        myCompletedProgressItems,
        loading: !!updatedItemsInProgress.length,
      }, shouldCheckProgress ? this.checkProgress : null);
    } catch (error) {
      displaySnackbar(i18n.t('reporting:errorFetchingExistingCsvStatuses'));
    }
  }

  handleStatusCheck = async (exportId) => {
    const {
      displaySnackbar
    } = this.props;
    try {
      const response = await fetchTtlCsvStatus(exportId);
      if (response.data.status === 'InProgress') {
        setTimeout(() => this.handleStatusCheck(exportId), 3000);
      }
      if (response.data.status === 'Completed') {
        this.getS3Status(false);
      }
      if (response.data.status === 'NotFound') {
        this.setState({ itemsInProgress: [] });
        displaySnackbar(`${i18n.t('reporting:notFound')} ${exportId}`);
      }
    } catch (error) {
      displaySnackbar(error.message);
    }
  }

  exportCsv = async () => {
    const {
      displaySnackbar
    } = this.props;
    try {
      const response = await fetchTtlCsvEntry();
      this.setState(prevState => {
        const updatedItemsInProgress = [...prevState.itemsInProgress];
        const updatedMyItems = [...prevState.myItems];
        updatedMyItems.unshift(response.data);
        updatedItemsInProgress.unshift(response.data);
        return {
          myItems: updatedMyItems,
          itemsInProgress: updatedItemsInProgress,
        }
      }, () => this.getS3Status(true));
    } catch (error) {
      displaySnackbar(error.message);
      this.setState({ loading: false });
    }
  }

  handleExportCsv = () =>
    this.setState({ loading: true, }, this.exportCsv);

  handleTabChange = (event, value) =>
    this.setState({ currentCsvDownloadTab: value });

  setProgress = (incomingProgress, filename) => {
    this.setState(prevState => {
      const updatedProgress = {
        ...prevState.progress,
        [filename]: incomingProgress, 
      };
     return { progress: updatedProgress  }
    });
  }

  handleListItemClick = async (event) => {
    const downloadlink = event.currentTarget.attributes.downloadlink.value;
    const filename = event.currentTarget.attributes.filename.value;
    const contentLength = event.currentTarget.attributes.contentlength.value;
    const response = await fetchTtlCsv(this.setProgress, downloadlink, filename, contentLength);
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
  }

  render() {
    const {
      completedItems,
      currentCsvDownloadTab,
      loading,
      myCompletedProgressItems,
      itemsInProgress,
      progress,
    } = this.state;

    return (
      <>
        <Grid container>
          <Paper className='blockContainer'>
            <h2>{i18n.t('reporting:ttlData')}</h2>
            <CircularProgressButton
              buttonText={i18n.t('reporting:ttlDataButton')}
              buttonOnClick={this.handleExportCsv}
              loading={loading}
              tooltipText={i18n.t('reporting:generateCsvHint')}
            />
          </Paper>
          <Paper className='blockContainer'>
            <h2>{i18n.t('reporting:exports')}</h2>
            <Box style={{ width: '100%'}}>
              <Box style={{ bordeBottom: 1, borderColor: 'divider' }}>
                <Tabs value={currentCsvDownloadTab} onChange={this.handleTabChange}>
                  <Tab label={i18n.t('reporting:inProgress')} />
                  <Tab label={i18n.t('reporting:completed')} />
                </Tabs>
              </Box>
              <TabPanel value={currentCsvDownloadTab} index={0}>
                {itemsInProgress.length || myCompletedProgressItems.length ? (
                  <List>
                    {itemsInProgress?.map(item =>
                      <InProgressListItem
                        key={`inProgress-${item.exportId}`}
                        primaryText={item.exportId}
                      />
                    )}
                    {myCompletedProgressItems?.map(item => 
                      <InProgressListItem
                        key={`inProgress-myCompleted-${item.filename}`}
                        primaryText={item.filename}
                        secondaryText={i18n.t('reporting:myProgressItemComplete')}
                        completed
                        secondaryAction={
                          <DownloadIconButton 
                            downloadLink={item.fileUrl}
                            contentLength={item.contentLength}
                            filename={item.filename}
                            onClick={this.handleListItemClick}
                            progress={progress[item.filename]}
                            className='reporting'
                          />
                        }
                      />
                    )}
                  </List>
                )
                    :
                  <div className='reportingProgress'>
                    {i18n.t('reporting:nothingInProgress')}
                  </div>
                    }
              </TabPanel>
              <TabPanel value={currentCsvDownloadTab} index={1}>
                {completedItems.length ? (
                  <List>
                    {completedItems.map(item => {
                      return (
                        <ActionListItem
                          key={`completed-${item.filename}`}
                          primaryText={item.filename}
                          secondaryAction={
                            <DownloadIconButton 
                              downloadLink={item.fileUrl}
                              contentLength={item.contentLength}
                              filename={item.filename}
                              onClick={this.handleListItemClick}
                              progress={progress[item.filename]}
                              className='reporting'
                            />
                          }
                        />)
                    })}
                  </List>
                )
                :
                  <div className='reportingProgress'>
                    {i18n.t('reporting:nothingCompleted')}
                  </div>
              }
              </TabPanel>
            </Box>
          </Paper>
        </Grid>
      </>
    );
  }
}

TimeToListReportGeneration.propTypes = {
  displaySnackbar: PropTypes.func.isRequired,
};

export default TimeToListReportGeneration;
